import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ReportsPage - Utilisation Reports
 *
 * Industrial control room aesthetic with data visualisation.
 * Features glowing bar charts and metric cards with status colours.
 * System Status panel shows component uptime with interactive timeline.
 */
import { useState } from 'react';
import { Box, Card, Grid, Group, Paper, SegmentedControl, SimpleGrid, Stack, Text, Title, Tooltip, } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@commander/api-client';
function useSystemStatus() {
    return useQuery({
        queryKey: ['reports', 'system-status'],
        queryFn: () => apiFetch('/api/reports/system-status'),
        staleTime: 60_000,
    });
}
function useUtilisation(groupBy) {
    return useQuery({
        queryKey: ['reports', 'utilisation', groupBy],
        queryFn: () => apiFetch(`/api/reports/utilisation?groupBy=${groupBy}`),
        staleTime: 60_000,
    });
}
/** Metric card with glowing accent */
function MetricCard({ label, value, suffix, colour = '#3B82F6', }) {
    return (_jsxs(Paper, { p: "md", style: {
            backgroundColor: 'var(--bg-card)',
            border: '1px solid var(--border-colour)',
            borderLeft: `3px solid ${colour}`,
            boxShadow: `inset 3px 0 12px -6px ${colour}40`,
        }, children: [_jsx(Text, { size: "xs", style: { color: 'var(--text-muted)' }, tt: "uppercase", fw: 600, mb: 4, children: label }), _jsxs(Group, { gap: 4, align: "baseline", children: [_jsx(Text, { size: "xl", fw: 700, ff: "monospace", style: { color: colour, textShadow: `0 0 20px ${colour}40` }, children: typeof value === 'number' ? value.toLocaleString() : value }), suffix && (_jsx(Text, { size: "sm", style: { color: 'var(--text-muted)' }, children: suffix }))] })] }));
}
/** Horizontal bar with glow effect */
function GlowBar({ value, max, colour = '#3B82F6', label, sublabel, }) {
    const percent = Math.min(100, (value / max) * 100);
    return (_jsxs(Box, { mb: "sm", children: [_jsxs(Group, { justify: "space-between", mb: 4, children: [_jsxs(Box, { children: [_jsx(Text, { size: "sm", fw: 500, style: { color: 'var(--text-primary)' }, children: label }), sublabel && (_jsx(Text, { size: "xs", style: { color: 'var(--text-muted)' }, children: sublabel }))] }), _jsx(Text, { size: "sm", ff: "monospace", fw: 600, style: { color: colour }, children: value.toLocaleString() })] }), _jsx(Box, { style: {
                    height: 8,
                    backgroundColor: 'var(--border-colour)',
                    borderRadius: 4,
                    overflow: 'hidden',
                }, children: _jsx(Box, { style: {
                        width: `${percent}%`,
                        height: '100%',
                        backgroundColor: colour,
                        borderRadius: 4,
                        boxShadow: `0 0 12px ${colour}60`,
                        transition: 'width 500ms ease',
                    } }) })] }));
}
/** Simple status colours - ChatGPT style */
const statusColours = {
    operational: '#10B981',
    degraded: '#F59E0B',
    outage: '#EF4444',
};
/** Warning icon for status indicator */
function WarningIcon({ colour }) {
    return (_jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", style: { flexShrink: 0 }, children: _jsx("path", { d: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z", stroke: colour, strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", fill: "none" }) }));
}
/** Simple status bar - ChatGPT style */
function StatusBar({ entry }) {
    const colour = statusColours[entry.status] ?? statusColours.operational;
    return (_jsx(Tooltip, { label: `${new Date(entry.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}: ${entry.uptimePercent.toFixed(1)}%`, position: "top", withArrow: true, children: _jsx(Box, { style: {
                height: 20,
                backgroundColor: colour,
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'opacity 150ms ease',
            }, onMouseEnter: (e) => {
                e.currentTarget.style.opacity = '0.7';
            }, onMouseLeave: (e) => {
                e.currentTarget.style.opacity = '1';
            } }) }));
}
/** ChatGPT-style System Status Panel - Clean and minimal */
function SystemStatusPanel({ data, isLoading }) {
    // Calculate date range
    const now = new Date();
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const formatMonth = (date) => date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    const dateRange = `${formatMonth(threeMonthsAgo)} - ${formatMonth(now)}`;
    if (isLoading) {
        return (_jsxs(Card, { withBorder: true, p: "lg", radius: "md", style: { backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-colour)' }, children: [_jsx(Text, { size: "lg", fw: 500, style: { color: 'var(--text-primary)' }, children: "System status" }), _jsx(Box, { h: 100, style: { display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Text, { style: { color: 'var(--text-muted)' }, children: "Loading..." }) })] }));
    }
    if (!data || data.length === 0) {
        return (_jsxs(Card, { withBorder: true, p: "lg", radius: "md", style: { backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-colour)' }, children: [_jsx(Group, { justify: "space-between", mb: "lg", children: _jsxs(Group, { gap: "xs", children: [_jsx(Text, { size: "lg", fw: 500, style: { color: 'var(--text-primary)' }, children: "System status" }), _jsx(Text, { size: "sm", style: { color: 'var(--text-muted)' }, children: dateRange })] }) }), _jsxs(Box, { style: {
                        padding: '32px 16px',
                        backgroundColor: 'var(--bg-base)',
                        borderRadius: 8,
                        border: '1px dashed var(--border-colour)',
                        textAlign: 'center',
                    }, children: [_jsx(Text, { size: "sm", style: { color: 'var(--text-muted)', marginBottom: 4 }, children: "No system status data available" }), _jsx(Text, { size: "xs", style: { color: 'var(--text-muted)', opacity: 0.7 }, children: "Status information will appear here once the backend is connected" })] })] }));
    }
    return (_jsxs(Card, { withBorder: true, p: "lg", radius: "md", style: { backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-colour)' }, children: [_jsx(Group, { justify: "space-between", mb: "lg", children: _jsxs(Group, { gap: "xs", children: [_jsx(Text, { size: "lg", fw: 500, style: { color: 'var(--text-primary)' }, children: "System status" }), _jsx(Text, { size: "sm", style: { color: 'var(--text-muted)' }, children: dateRange })] }) }), _jsx(Stack, { gap: "lg", children: data.map((component) => {
                    // Check if there are any non-operational entries
                    const hasIssues = component.entries.some(e => e.status !== 'operational');
                    const iconColour = hasIssues ? '#F59E0B' : '#10B981';
                    return (_jsxs(Box, { children: [_jsxs(Group, { justify: "space-between", mb: 8, children: [_jsxs(Group, { gap: 8, children: [_jsx(WarningIcon, { colour: iconColour }), _jsx(Text, { size: "sm", fw: 500, style: { color: 'var(--text-primary)' }, children: component.component })] }), _jsxs(Text, { size: "sm", style: { color: 'var(--text-muted)' }, children: [component.overallUptime.toFixed(2), "% uptime"] })] }), _jsx(Box, { style: {
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(90, 1fr)',
                                    gap: 1,
                                    width: '100%',
                                }, children: component.entries.slice(-90).map((entry, idx) => (_jsx(StatusBar, { entry: entry }, idx))) })] }, component.component));
                }) })] }));
}
/** Vertical bar chart for time series */
function TimeSeriesChart({ data }) {
    const maxSessions = Math.max(...data.map((d) => d.totalSessions), 1);
    return (_jsxs(Box, { style: { position: 'relative', height: 200 }, children: [_jsxs(Box, { style: {
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 24,
                    width: 40,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }, children: [_jsx(Text, { size: "xs", style: { color: 'var(--text-muted)' }, ff: "monospace", children: maxSessions }), _jsx(Text, { size: "xs", style: { color: 'var(--text-muted)' }, ff: "monospace", children: Math.round(maxSessions / 2) }), _jsx(Text, { size: "xs", style: { color: 'var(--text-muted)' }, ff: "monospace", children: "0" })] }), _jsx(Box, { style: {
                    marginLeft: 48,
                    height: '100%',
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 2,
                    paddingBottom: 24,
                }, children: data.slice(-14).map((item) => {
                    const height = (item.totalSessions / maxSessions) * 100;
                    const isWeekend = new Date(item.key).getDay() % 6 === 0;
                    return (_jsxs(Box, { style: {
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            height: '100%',
                            justifyContent: 'flex-end',
                        }, children: [_jsx(Box, { style: {
                                    width: '100%',
                                    height: `${height}%`,
                                    minHeight: 2,
                                    backgroundColor: isWeekend ? 'var(--text-muted)' : '#3B82F6',
                                    borderRadius: '2px 2px 0 0',
                                    boxShadow: isWeekend ? 'none' : '0 0 8px rgba(59, 130, 246, 0.4)',
                                    transition: 'height 500ms ease',
                                } }), _jsx(Text, { size: "xs", ff: "monospace", style: {
                                    position: 'absolute',
                                    bottom: 0,
                                    transform: 'rotate(-45deg)',
                                    transformOrigin: 'top left',
                                    whiteSpace: 'nowrap',
                                    fontSize: 9,
                                    color: 'var(--text-muted)',
                                }, children: item.label })] }, item.key));
                }) }), _jsx(Box, { style: {
                    position: 'absolute',
                    left: 48,
                    right: 0,
                    top: 0,
                    bottom: 24,
                    pointerEvents: 'none',
                }, children: [0, 1, 2].map((i) => (_jsx(Box, { style: {
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: `${i * 50}%`,
                        height: 1,
                        backgroundColor: 'var(--border-colour)',
                    } }, i))) })] }));
}
export function ReportsPage() {
    const [groupBy, setGroupBy] = useState('period');
    const { data, isLoading } = useUtilisation(groupBy);
    const { data: systemStatusData, isLoading: systemStatusLoading } = useSystemStatus();
    // Calculate summary metrics
    const totalSessions = data?.data.reduce((sum, d) => sum + d.totalSessions, 0) ?? 0;
    const totalHours = data?.data.reduce((sum, d) => sum + d.totalDurationHours, 0) ?? 0;
    const avgDuration = data?.data.length
        ? data.data.reduce((sum, d) => sum + d.averageDurationHours, 0) / data.data.length
        : 0;
    const uniqueUsers = data?.data.length
        ? Math.max(...data.data.map((d) => d.uniqueUsers))
        : 0;
    return (_jsxs(Stack, { gap: "lg", children: [_jsxs(Group, { justify: "space-between", align: "flex-start", children: [_jsxs(Box, { children: [_jsx(Title, { order: 2, style: { color: 'var(--text-primary)' }, children: "Utilisation Reports" }), _jsx(Text, { size: "sm", style: { color: 'var(--text-muted)' }, children: "Equipment utilisation and session analytics" })] }), _jsx(SegmentedControl, { value: groupBy, onChange: (value) => setGroupBy(value), data: [
                            { label: 'By Period', value: 'period' },
                            { label: 'By Equipment', value: 'equipment' },
                            { label: 'By Department', value: 'department' },
                        ], styles: {
                            root: { backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-colour)' },
                            label: { color: 'var(--text-secondary)' },
                            indicator: { backgroundColor: 'var(--blue-bg)', boxShadow: 'none' },
                            control: { border: 'none' },
                        } })] }), _jsxs(SimpleGrid, { cols: { base: 2, md: 4 }, spacing: "md", children: [_jsx(MetricCard, { label: "Total Sessions", value: totalSessions, colour: "#3B82F6" }), _jsx(MetricCard, { label: "Total Hours", value: Math.round(totalHours), suffix: "hrs", colour: "#22C55E" }), _jsx(MetricCard, { label: "Avg Duration", value: avgDuration.toFixed(1), suffix: "hrs", colour: "#F59E0B" }), _jsx(MetricCard, { label: "Active Users", value: uniqueUsers, colour: "#8B5CF6" })] }), _jsxs(Card, { withBorder: true, p: "lg", style: { backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-colour)' }, children: [_jsx(Title, { order: 4, mb: "md", style: { color: 'var(--text-primary)' }, children: groupBy === 'period'
                            ? 'Sessions Over Time'
                            : groupBy === 'equipment'
                                ? 'Sessions by Equipment'
                                : 'Sessions by Department' }), isLoading ? (_jsx(Box, { h: 200, style: { display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsx(Text, { style: { color: 'var(--text-muted)' }, children: "Loading..." }) })) : groupBy === 'period' ? (_jsx(TimeSeriesChart, { data: data?.data ?? [] })) : (_jsx(Grid, { children: data?.data.slice(0, 8).map((item) => {
                            const maxVal = Math.max(...(data?.data.map((d) => d.totalSessions) ?? [1]));
                            return (_jsx(Grid.Col, { span: { base: 12, sm: 6 }, children: _jsx(GlowBar, { value: item.totalSessions, max: maxVal, label: item.label, sublabel: `${item.totalDurationHours.toLocaleString()} hours`, colour: groupBy === 'department' ? '#8B5CF6' : '#3B82F6' }) }, item.key));
                        }) }))] }), _jsx(SystemStatusPanel, { data: systemStatusData, isLoading: systemStatusLoading })] }));
}
//# sourceMappingURL=ReportsPage.js.map